import { useEffect, useState } from 'react';
import { Container, Box, styled, Typography, Stack, useTheme, Chip } from '@mui/material';

// Components
import BreadcrumbWithBtnBack from 'src/components/BreadcrumbWithBtnBack';
import TabComponent from 'src/components/Tab/TabComponent';

// Utilities
import { TABS_PORT, TypeTabPort } from 'src/features/PortDetail/config';
import PortLogo from 'src/asset/icons/port-logo.svg';
import OverViewTab from 'src/features/PortDetail/Overview';
import OperationTab from 'src/features/PortDetail/Operation';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { useQueryPortDetail } from 'src/services/queries';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { isString } from 'lodash';
import TerminalTab from 'src/features/PortDetail/Terminals';
import TradeTransitTab from 'src/features/PortDetail/TradeTransit';
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import TrafficThroughputTab from 'src/features/PortDetail/TrafficThroughput';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const StyledChipHeader = styled(Chip)(({ theme }) => ({
  color: theme.palette.common.white,
  background: 'rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(4px)',
  '&:hover': {
    color: theme.palette.common.white,
    background: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(4px)',
  },
}));

const StyledChipBlack = styled(Chip)(({ theme }) => ({
  color: theme.palette.common.white,
  background: theme.palette.common.black,
  '&:hover': {
    color: theme.palette.common.white,
    background: theme.palette.common.black,
  },
}));

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  background: 'linear-gradient(180deg, #7551FF 0%, #3311DB 100%)',
  boxShadow: '0px 0px 50px 0px rgba(112, 114, 176, 0.08)',
}));

const PortDetail = () => {
  const theme = useTheme();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<string>(TypeTabPort.Overview);
  const { data, isFetching } = useQueryPortDetail(id);
  const portDetail = data?.data || null;

  useEffect(() => {
    if (!id) {
      navigate(AuthRouteKeys.Port);
    }
  }, [id]);

  const renderForm = () => {
    switch (selectedTab) {
      case TypeTabPort.Overview:
        return <OverViewTab portDetail={portDetail || null} />;
      case TypeTabPort.Operations:
        return <OperationTab portDetail={portDetail || null} />;
      case TypeTabPort.Terminals:
        return <TerminalTab portDetail={portDetail || null} />;
      case TypeTabPort.TradeTransit:
        return <TradeTransitTab portDetail={portDetail || null} />;
      case TypeTabPort.TrafficThroughput:
        return <TrafficThroughputTab portDetail={portDetail || null} />;
      default:
        return null;
    }
  };

  if (isFetching) {
    return (
      <StyledContainer maxWidth={'xl'}>
        <StyledBox>
          <LoadingSkeleton count={3} height={30} />
        </StyledBox>
        <Box mt={3} />
        <LoadingSkeleton count={10} height={40} />
      </StyledContainer>
    );
  }

  return (
    <StyledContainer maxWidth={'xl'}>
      <BreadcrumbWithBtnBack
        listItem={[
          {
            text: 'Trade Data',
          },
          {
            text: 'China',
          },
          {
            text: 'MAEU720035747',
          },
          {
            text: 'Shanghai CN SHA',
          },
        ]}
        updatedAt=""
      />
      <SectionErrorBoundary resetKey="PortInformation" sectionName="PortInformation">
        <StyledBox>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2.5}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  backgroundColor: theme.palette.background.default,
                  borderRadius: theme.shape.borderRadius / 4,
                  p: 0.5,
                }}
                width={48}
                mr={1}
                src={PortLogo}
                component="img"
              />
              <Stack direction="column" spacing={1}>
                <Stack direction="row" spacing={1}>
                  <Typography variant="h5" color="inheritWhite.main" align="left">
                    {renderTextOrDefault(portDetail?.meta?.name)} {renderTextOrDefault(portDetail?.meta?.locode)}
                  </Typography>
                  <StyledChipBlack
                    label={`${renderNumberOrDefault(portDetail?.meta?.global_trade || 0)}% of Global Trade`}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {getArrayDefault(portDetail?.composition).map((item, index) => (
                    <StyledChipHeader
                      key={`composition-${index}-${item.name}`}
                      label={`${renderTextOrDefault(item?.name)}: ${isString(item?.value) ? renderTextOrDefault(item?.value) : `${renderTextOrDefault(item?.value)}%`}`}
                    />
                  ))}
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}></Stack>
          </Stack>
        </StyledBox>
      </SectionErrorBoundary>
      <TabComponent
        tabs={TABS_PORT}
        selected={selectedTab}
        handleChange={(e, value) => {
          setSelectedTab(value);
        }}
      />
      <Box mt={3} />
      <SectionErrorBoundary resetKey={selectedTab} sectionName={`PortDetail - ${selectedTab}`}>
        {renderForm()}
      </SectionErrorBoundary>
    </StyledContainer>
  );
};

export default PortDetail;
